import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import React from 'react';
import { Link } from 'react-router-dom';

function AnimatedCard({
  outcome,
  index,
}: {
  outcome: { icon: string; text: string };
  index: number;
}) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`w-full bg-white/95 rounded-lg px-6 py-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-all ease-out ${
        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
      }`}
      style={{
        transitionDuration: '600ms',
        transitionDelay: isVisible ? `${index * 90}ms` : '0ms',
      }}
    >
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#BA3D03]/10 flex items-center justify-center">
        <i
          className={`${outcome.icon} text-[#BA3D03] text-lg leading-none`}
          style={{ fontSize: '18px', lineHeight: 1 }}
        />
      </div>
      <p className="text-base text-[#0E0503] font-medium leading-snug">{outcome.text}</p>
    </div>
  );
}

function AnimatedCTAs() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`pt-2 flex flex-wrap items-center gap-4 transition-all ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{
        transitionDuration: '600ms',
        transitionDelay: isVisible ? '120ms' : '0ms',
      }}
    >
      <Link
        to="/course-overview"
        className="group inline-flex items-center gap-2 bg-[#BA3D03] hover:bg-[#9A3203] text-white text-sm font-semibold px-4 py-3 rounded-md border border-[#BA3D03] hover:border-[#9A3203] transition-all duration-200 whitespace-nowrap cursor-pointer shadow-md overflow-hidden"
      >
        <span>Explore the Course Overview</span>
        <span className="inline-flex items-center transition-transform duration-300 group-hover:translate-x-1">
          <i className="ri-arrow-right-line text-base" />
        </span>
      </Link>
    </div>
  );
}

export default function OrganisationalOutcomes() {
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation({ threshold: 0.1 });

  const outcomes = [
    {
      icon: 'ri-shield-star-line',
      text: 'Reduced cultural and reputational risk in project delivery',
    },
    {
      icon: 'ri-group-line',
      text: 'Clearer culturally safe engagement with Traditional Custodians and community stakeholders',
    },
    {
      icon: 'ri-book-open-line',
      text: 'ICIP aware practice and documentation you can apply on real projects',
    },
    {
      icon: 'ri-git-branch-line',
      text: 'Earlier integration of cultural knowledge into planning and delivery decisions',
    },
    {
      icon: 'ri-stack-line',
      text: 'Practical tools and templates to support Country centred delivery across the project lifecycle',
    },
  ];

  return (
    <section
      id="organisational-outcomes"
      className="relative py-24 lg:py-32 overflow-hidden"
      style={{
        paddingBottom: 'calc(var(--sticky-footer-h, 72px) + 24px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      {/* Background landscape photo */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://static.readdy.ai/image/597b2346b2ff492b6c1bd141556b2fdd/767301a851b963cdeebe5784a57ff3ed.jpeg"
          alt="Aboriginal dot painting by Jason Douglas, Dalmarri 2025"
          className="w-full h-full object-cover object-center"
          style={{ filter: 'saturate(90%) contrast(95%)', transform: 'scale(1.02)' }}
        />
        <div className="absolute inset-0" style={{ background: 'rgba(245, 220, 195, 0.72)' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left column */}
          <div
            ref={leftRef as React.RefObject<HTMLDivElement>}
            className={`relative transition-all duration-700 ease-out ${
              leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <div
              className="rounded-2xl px-6 sm:px-10 py-8 sm:py-10"
              style={{ background: 'rgba(255, 255, 255, 0.88)', backdropFilter: 'blur(2px)' }}
            >
              <div className="flex gap-0">
                <div className="flex-shrink-0 w-1.5 bg-[#BA3D03] rounded-full mr-5 sm:mr-8" />
                <div>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0E0503] leading-tight">
                    What your organisation gains
                  </h2>
                  <p className="mt-4 sm:mt-5 text-base text-[#3D1203]/80 leading-relaxed max-w-md">
                    Practical outcomes for NSW project teams delivering built environment work aligned with Better Placed and Connecting with Country.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right column — individually animated cards */}
          <div className="space-y-4">
            {outcomes.map((outcome, index) => (
              <AnimatedCard key={index} outcome={outcome} index={index} />
            ))}
            <AnimatedCTAs />
          </div>
        </div>
      </div>

      {/* Artwork attribution */}
      <p className="absolute bottom-4 right-4 text-[#3D1203]/50 text-xs font-light tracking-wide pointer-events-none select-none">
        Source: Jason Douglas, Dalmarri 2025
      </p>
    </section>
  );
}
